import axios from 'axios';

import { HttpStatusCode } from '@framework/constants/enums/http';

import {
    type AuthenticationStatusPending,
    SecondaryProofType,
    type SecondFactorStatusItem,
    AuthenticationErrorCode
} from '@accounts/types/authentication';
import { type AuthenticationError } from '@accounts/types/store';

import { type ApiErrorResponse } from '@type/api';

import type { AxiosError } from 'axios';

const hasAnyTfaMethodEnrolled = (
    secondFactors: SecondFactorStatusItem[] = [],
    exclude: SecondaryProofType[] = []
) => Boolean(secondFactors?.some((item) => item.isEnrolled && !exclude.includes(item.id)));

const hasAnyTfaMethodEnabled = (
    secondFactors: SecondFactorStatusItem[],
    exclude: SecondaryProofType[] = []
) => Boolean(secondFactors?.some((item) => item.canUse && !exclude.includes(item.id)));

const canUseTfaMethod = (secondFactors: SecondFactorStatusItem[], method?: SecondaryProofType) => {
    return Boolean(secondFactors?.some((item) => item.canUse && (!method || item.id === method)));
};
const isTfaMethodEnrolled = (
    secondFactors: SecondFactorStatusItem[],
    method?: SecondaryProofType
) => Boolean(secondFactors?.some((item) => item.isEnrolled && item.id === method));

export const buildUserTfaStatus = (secondFactors: SecondFactorStatusItem[] = []) => {
    const isOneTouchEnrolled = isTfaMethodEnrolled(secondFactors, SecondaryProofType.OneTouch);
    const isOneTouchEnabled = canUseTfaMethod(secondFactors, SecondaryProofType.OneTouch);
    const isMtanEnrolled = isTfaMethodEnrolled(secondFactors, SecondaryProofType.Mtan);
    const isMtanEnabled = canUseTfaMethod(secondFactors, SecondaryProofType.Mtan);
    const isAnyTfaMethodEnrolled = hasAnyTfaMethodEnrolled(secondFactors);
    const isAnyTfaMethodEnabled = hasAnyTfaMethodEnabled(secondFactors);
    const isUserBlockedToManageTfa = !isAnyTfaMethodEnabled;

    return {
        isOneTouchEnrolled,
        isOneTouchEnabled,
        isUserBlockedToManageTfa,
        isMtanEnrolled,
        isMtanEnabled,
        isAnyTfaMethodEnrolled,
        isAnyTfaMethodEnabled
    };
};

export const getPendingSecondaryProof = (
    pending: AuthenticationStatusPending[],
    type: SecondaryProofType
) => {
    return pending?.find((item) => item.proves === type && item.state === 'pending');
};

export interface ThrottleErrorResponse {
    wait: number;
}

export const getThrottleAction = (error: AxiosError<ThrottleErrorResponse>) => {
    const waitTimeInSeconds = error?.response?.data?.wait as number;
    const waitTimeInMinutes = (waitTimeInSeconds > 60 && Math.floor(waitTimeInSeconds / 60)) || 0;
    const waitTimeInHours = (waitTimeInSeconds > 3600 && Math.floor(waitTimeInSeconds / 3600)) || 0;

    let errorAction: AuthenticationError;
    if (waitTimeInHours) {
        errorAction = {
            code: AuthenticationErrorCode.ThrottleHours,
            params: {
                hours: waitTimeInHours
            }
        };
    } else if (waitTimeInMinutes) {
        errorAction = {
            code: AuthenticationErrorCode.ThrottleMinutes,
            params: {
                minutes: waitTimeInMinutes
            }
        };
    } else {
        errorAction = {
            code: AuthenticationErrorCode.ThrottleSeconds,
            params: {
                seconds: waitTimeInSeconds
            }
        };
    }

    return errorAction;
};

export const getLoginErrorCode = (error: AxiosError<ApiErrorResponse>) => {
    if (!axios.isAxiosError(error)) return error;

    const status = error.response?.status;
    if (status === HttpStatusCode.BadRequest)
        return {
            ...error,
            code: error.response?.data?.errors?.email
                ? AuthenticationErrorCode.InvalidEmail
                : error.response?.data?.code
        };
    if (status === HttpStatusCode.TooManyRequests)
        return {
            ...error,
            ...getThrottleAction(error as unknown as AxiosError<ThrottleErrorResponse>)
        };

    return { ...error, code: error?.code ?? error.response?.data?.code };
};
